import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { Calculator } from '../components/Calculator';
import { ProductSearchModal } from '../components/ProductSearchModal';
import { ReceiptModal } from '../components/ReceiptModal';
import { openCashDrawer } from '../lib/silentPrint';
import { PreSalesModal } from '../components/PreSalesModal';
import { PDVReceivablesModal } from '../components/PDVReceivablesModal';
import { DeliveryModal } from '../components/DeliveryModal';
import { SwitchOperatorModal } from '../components/SwitchOperatorModal';
import { CloseShiftModal } from '../components/CloseShiftModal';
import { PDVCartList } from '../components/pdv/PDVCartList';
import { PDVSidebar } from '../components/pdv/PDVSidebar';
import { PDVPaymentModal } from '../components/pdv/PDVPaymentModal';
import { PDVCupomModal } from '../components/pdv/PDVCupomModal';
import { SangriaModal, SuprimentoModal } from '../components/pdv/SangriaModal';
import { CreditLimitOverrideModal } from '../components/pdv/CreditLimitOverrideModal';
import { printFiadoVoucher } from '../components/pdv/printFiadoVoucher';
import { CancelItemModal, CancelSaleModal, NavBlockModal, DiscountModal, ReverseLastModal, PixModal, DeleteItemPasswordModal } from '../components/pdv/PDVMiscModals';
import { smartFill } from '../components/pdv/paymentFill';
import { useCompanySettings } from '../hooks/useCompanySettings';
import { useAuth } from '../contexts/AuthContext';
import { usePDVContext } from '../contexts/PDVContext';
import { useTheme } from '../contexts/ThemeContext';
import { AlertTriangle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PDVPage = () => {
  const company = useCompanySettings();
  const { user } = useAuth();
  const { theme: pdvTheme } = useTheme();
  const { setHasUnsavedSale, registerBlockHandler, unregisterBlockHandler } = usePDVContext();

  // ============== ESTADO ==============
  const CART_STORAGE_KEY = user ? `sf_pdv_cart_${user.id}` : null;
  const [cart, setCart] = useState([]);
  const [cartHydrated, setCartHydrated] = useState(false);
  // cupomType agora é escolhido no modal PÓS-pagamento (não mais antes)
  const [cupomType, setCupomType] = useState('nao_fiscal');
  const [showCupomModal, setShowCupomModal] = useState(false);
  const [pendingSaleContext, setPendingSaleContext] = useState(null);
  // Modo NF-e ativo — quando true, pula o modal Fiscal/NãoFiscal e emite NF-e direto
  const [nfeMode, setNfeMode] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('dinheiro');
  // Ref síncrona do método selecionado — evita closure desatualizada em setas rápidas
  const paymentMethodRef = useRef(paymentMethod);
  paymentMethodRef.current = paymentMethod;
  const [onCredit, setOnCredit] = useState(false);
  const [creditDueDate, setCreditDueDate] = useState('');
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerHoverIdx, setCustomerHoverIdx] = useState(-1);
  const [customerName, setCustomerName] = useState('');
  const [customerCpf, setCustomerCpf] = useState('');
  // Volta para "Consumidor Final" (limpa seleção, nome, CPF e o texto de busca)
  const resetCustomer = () => { setSelectedCustomer(null); setCustomerName(''); setCustomerCpf(''); setCustomerSearch(''); setCustomerHoverIdx(-1); };
  const [discount, setDiscount] = useState(0);
  const [discountInput, setDiscountInput] = useState('');
  const [discountReason, setDiscountReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [pixData, setPixData] = useState(null);
  const [showCalculator, setShowCalculator] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [showPreSalesModal, setShowPreSalesModal] = useState(false);
  const [loadedPreSaleId, setLoadedPreSaleId] = useState(null);
  const [loadedTableNumber, setLoadedTableNumber] = useState(null);
  const [showCancelSaleModal, setShowCancelSaleModal] = useState(false);
  const [cancelSaleReason, setCancelSaleReason] = useState('');
  const [cancelSalePassword, setCancelSalePassword] = useState('');
  const [pendingProceed, setPendingProceed] = useState(null);
  const [showReverseLastModal, setShowReverseLastModal] = useState(false);
  const [reverseLastReason, setReverseLastReason] = useState('');
  const [reverseLastPreview, setReverseLastPreview] = useState(null);
  const [reverseLoading, setReverseLoading] = useState(false);
  const [showReceivablesModal, setShowReceivablesModal] = useState(false);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [showSwitchOperator, setShowSwitchOperator] = useState(false);
  const [showCloseShift, setShowCloseShift] = useState(false);
  const [showSangriaModal, setShowSangriaModal] = useState(false);
  const [showSuprimentoModal, setShowSuprimentoModal] = useState(false);
  const [delivery, setDelivery] = useState(null);
  const [paymentInputs, setPaymentInputs] = useState({
    dinheiro: '', pix: '', pix_maquininha: '', credito: '', debito: '', voucher: '', cheque: '', a_prazo: ''
  });
  // Ref síncrona dos inputs — garante valores frescos em setas/atalhos rápidos (imune a stale closure)
  const paymentInputsRef = useRef(paymentInputs);
  paymentInputsRef.current = paymentInputs;
  const [barcodeInput, setBarcodeInput] = useState('');
  const [pendingWeight, setPendingWeight] = useState(null); // peso digitado com "*" antes de escolher o produto
  const [stockWarning, setStockWarning] = useState(null); // { product, scaleWeight, scalePrice } aviso de estoque zerado
  const pendingWeightRef = useRef(null);
  pendingWeightRef.current = pendingWeight;
  const focusBarcode = useCallback(() => {
    setTimeout(() => {
      const el = document.querySelector('[data-testid="barcode-input-side"]');
      if (el) { el.focus(); try { el.select(); } catch (_) {} }
    }, 60);
  }, []);
  const [receiptSale, setReceiptSale] = useState(null);
  const pixPendingRef = useRef(null); // venda PIX aguardando confirmação: { ctx, chosenType, shouldPrint, overridePassword } → grava a venda ao confirmar
  const [cancelItemTarget, setCancelItemTarget] = useState(null);
  const [cancelItemReason, setCancelItemReason] = useState('');
  const [cancelItemPassword, setCancelItemPassword] = useState('');
  const [deleteItemTarget, setDeleteItemTarget] = useState(null); // { index, item } — exclusão via tecla Delete
  const [deleteItemPassword, setDeleteItemPassword] = useState('');
  const [deleteItemError, setDeleteItemError] = useState('');
  const [cancelItemError, setCancelItemError] = useState('');
  const [creditOverride, setCreditOverride] = useState({ show: false, info: null, ctx: null, chosenType: null, password: '', error: '' });

  const barcodeBufferRef = useRef('');
  const lastKeyTimeRef = useRef(0);

  // Seleção por teclado na lista de itens (setas ↑↓ navegam, Delete remove)
  const cartRef = useRef(cart);
  cartRef.current = cart;
  const [selectedItemIndex, setSelectedItemIndex] = useState(-1);
  const selectedIndexRef = useRef(-1);
  selectedIndexRef.current = selectedItemIndex;
  // Ref sempre fresca indicando que a tela base do PDV está ativa (nenhum modal aberto)
  const baseScreenRef = useRef(true);
  baseScreenRef.current = !showCalculator && !showSearch && !showPaymentModal && !pixData &&
    !showDiscountModal && !showCancelSaleModal && !showReverseLastModal && !showPreSalesModal &&
    !showReceivablesModal && !showDeliveryModal && !showSwitchOperator && !showCloseShift &&
    !showSangriaModal && !showSuprimentoModal && !cancelItemTarget && !creditOverride.show && !showCupomModal && !deleteItemTarget;
  const pdvBaseActive = baseScreenRef.current;

  const PAYMENT_ORDER = ['dinheiro', 'pix', 'pix_maquininha', 'credito', 'debito', 'voucher', 'cheque', 'a_prazo'];

  // ============== FUNÇÕES CALCULADAS ==============
  const activeItems = cart.filter(i => !i.cancelled);
  const cancelledItems = cart.filter(i => i.cancelled);
  const hasActiveItems = cart.some(i => !i.cancelled);

  const calculateSubtotal = () => cart.filter(i => !i.cancelled).reduce((sum, item) => sum + (Number(item.subtotal) || 0), 0);
  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const finalDiscount = Math.min(discount, subtotal);
    return Math.max(0, subtotal - finalDiscount);
  };

  // Ordem canônica das formas de pagamento — DEVE bater com a lista `methods` do PDVPaymentModal.
  // (a navegação por setas/grade vive no modal via moveFocus)

  // ============== EFFECTS: HYDRATE + PERSIST CART ==============
  useEffect(() => {
    if (!CART_STORAGE_KEY || cartHydrated) return;
    try {
      const raw = localStorage.getItem(CART_STORAGE_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        if (Array.isArray(saved) && saved.length > 0) setCart(saved);
      }
    } catch { /* corrupted */ }
    setCartHydrated(true);
     
  }, [CART_STORAGE_KEY]);

  useEffect(() => {
    if (!CART_STORAGE_KEY || !cartHydrated) return;
    try {
      if (cart.length === 0) localStorage.removeItem(CART_STORAGE_KEY);
      else localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
    } catch { /* quota */ }
  }, [cart, CART_STORAGE_KEY, cartHydrated]);

  // Mantém a seleção válida quando o carrinho muda (item removido/cancelado ou carrinho limpo)
  useEffect(() => {
    setSelectedItemIndex((prev) => (prev >= 0 && (!cart[prev] || cart[prev].cancelled)) ? -1 : prev);
  }, [cart]);

  // ============== EFFECTS: PAYMENT MODAL ==============
  useEffect(() => {
    if (!showPaymentModal) return;
    // Fluxo em duas fases: começa na SELEÇÃO, sem preencher valores.
    // O foco/roleta é gerenciado pelo próprio PDVPaymentModal.
    setOnCredit(false);
    setPaymentMethod('dinheiro');
    setPaymentInputs({
      dinheiro: '', pix: '', pix_maquininha: '', credito: '', debito: '', voucher: '', cheque: '', a_prazo: '',
    });
     
  }, [showPaymentModal]);

  // O teclado do Modal de Pagamento agora vive no próprio PDVPaymentModal
  // (fluxo em duas fases: SELEÇÃO com setas ↑↓ → VALOR com Enter).

  // ============== EFFECTS: PDV CONTEXT ==============
  useEffect(() => { setHasUnsavedSale(hasActiveItems); }, [hasActiveItems, setHasUnsavedSale]);
  useEffect(() => () => { setHasUnsavedSale(false); setPendingProceed(null); }, [setHasUnsavedSale]);
  useEffect(() => {
    registerBlockHandler((proceed) => setPendingProceed(() => proceed));
    return () => unregisterBlockHandler();
  }, [registerBlockHandler, unregisterBlockHandler]);

  useEffect(() => {
    const beforeUnload = (e) => {
      if (hasActiveItems) { e.preventDefault(); e.returnValue = ''; return ''; }
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [hasActiveItems]);

  // ============== EFFECTS: LOAD CUSTOMERS ==============
  useEffect(() => {
    axios.get(`${API}/customers`, { withCredentials: true }).then(r => setCustomers(r.data)).catch(() => {});
  }, []);

  // ============== CART HANDLERS ==============
  const addProductToCart = useCallback((product, scaleWeight = null, scalePrice = null, bypassStock = false) => {
    // Peso pendente digitado com "*" (ex.: 0,985*) tem prioridade como quantidade fracionada.
    if (scaleWeight == null && scalePrice == null && pendingWeightRef.current != null) {
      scaleWeight = pendingWeightRef.current;
      pendingWeightRef.current = null;
      setPendingWeight(null);
    }
    // Aviso de estoque zerado/negativo: NÃO bloqueia a venda, mas pede confirmação do operador.
    if (!bypassStock && Number(product.stock_quantity) <= 0) {
      setStockWarning({ product, scaleWeight, scalePrice });
      return;
    }
    // Etiqueta de balança com PREÇO embutido: o valor a cobrar já vem no código.
    // Grava o PESO real como quantidade (valor ÷ preço/kg) e preço unitário = preço/kg cadastrado,
    // mas TRAVA o subtotal no valor da etiqueta (o caixa não pode variar por arredondamento).
    if (scalePrice != null && scalePrice > 0) {
      const unitKg = Number(product.price) || 0;
      const weight = unitKg > 0 ? Math.round((scalePrice / unitKg) * 1000) / 1000 : 1;
      setCart(prev => [...prev, {
        product_id: product.id,
        product_name: product.name,
        code: product.code || product.barcode || '',
        barcode: product.barcode || '',
        quantity: weight,
        unit_price: unitKg > 0 ? unitKg : scalePrice,
        cost_price: product.cost_price || 0,
        category: product.category,
        unit: product.unit || 'kg',
        subtotal: scalePrice,
        catalog_price: unitKg,
        scale_price_mode: true,
        no_stock: Number(product.stock_quantity) <= 0,
        cancelled: false
      }]);
      return;
    }
    const quantity = scaleWeight || 1;
    const existingItem = cart.find(item => item.product_id === product.id && !item.cancelled);
    if (existingItem && !scaleWeight) {
      setCart(cart.map(item =>
        item.product_id === product.id && !item.cancelled
          ? { ...item, quantity: item.quantity + 1, subtotal: (item.quantity + 1) * item.unit_price }
          : item
      ));
    } else {
      setCart(prev => [...prev, {
        product_id: product.id,
        product_name: product.name,
        code: product.code || product.barcode || '',
        barcode: product.barcode || '',
        quantity,
        unit_price: product.price,
        cost_price: product.cost_price || 0,
        category: product.category,
        unit: product.unit || 'un',
        subtotal: product.price * quantity,
        no_stock: Number(product.stock_quantity) <= 0,
        cancelled: false
      }]);
    }
  }, [cart]);

  const fetchByBarcode = useCallback(async (barcode) => {
    if (!barcode || barcode.length < 3) return;
    try {
      const { data } = await axios.get(`${API}/products/barcode/${barcode}`, { withCredentials: true });
      if (data.product) { addProductToCart(data.product, data.scale_weight, data.scale_price); setError(''); }
    } catch (err) {
      try {
        const r = await axios.get(`${API}/products/code/${encodeURIComponent(barcode)}`, { withCredentials: true });
        if (r.data) { addProductToCart(r.data); setError(''); return; }
      } catch (_e) { /* not found */ }
      setError(`Produto com código ${barcode} não encontrado`);
      setTimeout(() => setError(''), 2500);
    }
  }, [addProductToCart]);

  const fetchByCode = useCallback(async (code, qty) => {
    try {
      const { data } = await axios.get(`${API}/products/code/${encodeURIComponent(code)}`, { withCredentials: true });
      if (data) { addProductToCart(data, qty); setError(''); }
    } catch (err) {
      setError(`Produto com código '${code}' não encontrado`);
      setTimeout(() => setError(''), 2500);
    }
  }, [addProductToCart]);

  const processInput = useCallback((raw) => {
    let value = (raw || '').trim();
    if (!value) return;
    if (/^[.,]/.test(value)) value = '0' + value;
    // Peso pendente: operador digita o peso e pressiona "*" (nada após) para armar
    // a próxima venda fracionada. Ex.: "0,985*" -> arma 0.985 kg; depois escolhe o produto.
    const pw = value.match(/^(\d+[.,]?\d*)\s*\*\s*$/);
    if (pw) {
      const w = parseFloat(pw[1].replace(',', '.'));
      if (!isFinite(w) || w <= 0) {
        setError('Peso inválido');
        setTimeout(() => setError(''), 2500);
        return;
      }
      setPendingWeight(w);
      pendingWeightRef.current = w;
      return;
    }
    const m = value.match(/^(\d+[.,]?\d*)\s*[*xX]\s*(.+)$/);
    if (m) {
      const qty = parseFloat(m[1].replace(',', '.'));
      const code = m[2].trim();
      if (!isFinite(qty) || qty <= 0) {
        setError('Quantidade inválida');
        setTimeout(() => setError(''), 2500);
        return;
      }
      // Quantidade explícita descarta qualquer peso armado para evitar aplicação indevida.
      pendingWeightRef.current = null; setPendingWeight(null);
      fetchByCode(code, qty); return;
    }
    if (/^\d{1,6}$/.test(value)) {
      if (pendingWeightRef.current != null) {
        const w = pendingWeightRef.current;
        pendingWeightRef.current = null;
        setPendingWeight(null);
        fetchByCode(value, w);
      } else {
        fetchByCode(value, 1);
      }
      return;
    }
    fetchByBarcode(value);
  }, [fetchByCode, fetchByBarcode]);

  // ============== EFFECTS: F-KEYS + BARCODE ==============
  useEffect(() => {
    const handleFKeys = (e) => {
      const k = e.key;
      const isInputField = e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA';
      // F2: foco imediato no campo de Código de Barras (seleciona texto existente), só na tela base
      if (k === 'F2') {
        e.preventDefault(); e.stopPropagation();
        e.stopImmediatePropagation && e.stopImmediatePropagation();
        if (baseScreenRef.current) {
          const el = document.querySelector('[data-testid="barcode-input-side"]');
          if (el) { el.focus(); try { el.select(); } catch (_) {} }
        }
        return false;
      }
      if (k === 'F4' || k === 'F5' || k === 'F7' || k === 'F8' || k === 'F9' || k === 'F10' || k === 'F11') {
        e.preventDefault(); e.stopPropagation();
        e.stopImmediatePropagation && e.stopImmediatePropagation();
        if (k === 'F4' && !showCalculator && !showPaymentModal && !pixData) setShowSearch(true);
        else if (k === 'F5' && !showSearch && !showPaymentModal && !pixData) setShowCalculator(true);
        else if (k === 'F7' && !showCalculator && !showSearch && !showPaymentModal && !pixData) setShowReceivablesModal(true);
        else if (k === 'F8' && cart.length > 0 && !showCalculator && !showSearch && !showPaymentModal && !pixData) setShowDiscountModal(true);
        else if (k === 'F9' && !showCalculator && !showSearch && !showPaymentModal && !pixData && !showReverseLastModal) openReverseLastSale();
        else if (k === 'F10' && cart.length > 0 && !showCalculator && !showSearch && !showPaymentModal && !pixData) setShowPaymentModal(true);
        else if (k === 'F11' && !showCalculator && !showSearch && !showPaymentModal && !pixData) setShowPreSalesModal(true);
        return false;
      }

      // Navegação por teclado na lista de itens + exclusão com Delete
      if ((k === 'ArrowDown' || k === 'ArrowUp' || k === 'Delete') && baseScreenRef.current) {
        const items = cartRef.current;
        const activeIdxs = items.map((it, i) => (it.cancelled ? -1 : i)).filter(i => i >= 0);
        if (activeIdxs.length === 0) return;
        if (k === 'Delete') {
          const inputHasText = isInputField && e.target.value && e.target.value.length > 0;
          if (inputHasText) return; // permite apagar texto do input normalmente
          e.preventDefault(); e.stopPropagation();
          const idx = selectedIndexRef.current;
          if (idx < 0 || !items[idx] || items[idx].cancelled) return false;
          // Abre modal de senha; a exclusão só ocorre após digitar a senha e Enter
          setDeleteItemTarget({ index: idx, item: items[idx] });
          setDeleteItemPassword('');
          setDeleteItemError('');
          return false;
        }
        e.preventDefault(); e.stopPropagation();
        const cur = selectedIndexRef.current;
        let pos = activeIdxs.indexOf(cur);
        if (k === 'ArrowDown') pos = pos < 0 ? 0 : Math.min(pos + 1, activeIdxs.length - 1);
        else pos = pos < 0 ? activeIdxs.length - 1 : Math.max(pos - 1, 0);
        setSelectedItemIndex(activeIdxs[pos]);
        return false;
      }

      const isBarcodeInput = e.target?.dataset?.testid === 'barcode-input-side';
      const barcodeIsEmpty = isBarcodeInput && (!e.target.value || e.target.value.length === 0);
      const lettersAllowed = (!isInputField || barcodeIsEmpty) && !e.ctrlKey && !e.metaKey && !e.altKey;
      if (lettersAllowed) {
        const key = k.toLowerCase();
        const blockAndDo = (fn) => { e.preventDefault(); e.stopPropagation(); fn(); return false; };
        if (key === 'f' && cart.length > 0 && !showCalculator && !showSearch && !showPaymentModal && !pixData) return blockAndDo(() => setShowPaymentModal(true));
        if (key === 'b' && !showCalculator && !showPaymentModal && !pixData) return blockAndDo(() => setShowSearch(true));
        if (key === 'c' && cart.length > 0 && !showCalculator && !showSearch && !showPaymentModal && !pixData && !showCancelSaleModal) return blockAndDo(() => setShowCancelSaleModal(true));
        if (key === 'd' && cart.length > 0 && !showCalculator && !showSearch && !showPaymentModal && !pixData) return blockAndDo(() => setShowDiscountModal(true));
        if (key === 'r' && !showCalculator && !showSearch && !showPaymentModal && !pixData) return blockAndDo(() => setShowReceivablesModal(true));
        if (key === 'e' && !showCalculator && !showSearch && !showPaymentModal && !pixData && !showReverseLastModal) return blockAndDo(() => openReverseLastSale());
        if (key === 'm' && !showCalculator && !showSearch && !showPaymentModal && !pixData && !showPreSalesModal) return blockAndDo(() => setShowPreSalesModal(true));
      }
    };
    const handleBarcode = (e) => {
      if (showCalculator || showSearch || showPaymentModal || pixData) return;
      const target = e.target;
      const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA';
      if (isInput) return;
      const now = Date.now();
      if (now - lastKeyTimeRef.current > 100) barcodeBufferRef.current = '';
      lastKeyTimeRef.current = now;
      if (/^[0-9]$/.test(e.key)) barcodeBufferRef.current += e.key;
      else if (e.key === 'Enter' && barcodeBufferRef.current.length >= 4) {
        const code = barcodeBufferRef.current;
        barcodeBufferRef.current = '';
        fetchByBarcode(code);
      } else if (!['Shift', 'Control', 'Alt', 'F2', 'F4', 'F5', 'F7', 'F8', 'F9', 'F10', 'F11'].includes(e.key)) {
        barcodeBufferRef.current = '';
      }
    };
    window.addEventListener('keydown', handleFKeys, { capture: true });
    document.addEventListener('keydown', handleFKeys, { capture: true });
    window.addEventListener('keydown', handleBarcode);
    return () => {
      window.removeEventListener('keydown', handleFKeys, { capture: true });
      document.removeEventListener('keydown', handleFKeys, { capture: true });
      window.removeEventListener('keydown', handleBarcode);
    };
     
  }, [cart.length, showCalculator, showSearch, showPaymentModal, pixData, fetchByBarcode]);

  const handleManualBarcode = (e) => {
    if (e.key === 'Enter' && barcodeInput.trim()) {
      processInput(barcodeInput.trim());
      setBarcodeInput('');
    }
  };

  // Auto-foco no campo de Código de Barras ao entrar na tela base (abrir PDV / fechar qualquer modal)
  useEffect(() => {
    if (pdvBaseActive) {
      const el = document.querySelector('[data-testid="barcode-input-side"]');
      if (el && document.activeElement !== el) { el.focus(); try { el.select(); } catch (_) {} }
    }
  }, [pdvBaseActive]);

  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) return;
    setCart(cart.map(item =>
      item.product_id === productId && !item.cancelled
        ? { ...item, quantity: newQuantity, subtotal: newQuantity * item.unit_price }
        : item
    ));
  };

  const cancelItem = (productId) => {
    setCancelItemTarget(productId);
    setCancelItemReason('');
    setCancelItemPassword('');
    setCancelItemError('');
  };

  const confirmCancelItem = async () => {
    setCancelItemError('');
    if (!cancelItemReason.trim() || cancelItemReason.trim().length < 3) {
      setCancelItemError('Informe o motivo do cancelamento (mín. 3 caracteres).');
      return;
    }
    if (!cancelItemPassword) { setCancelItemError('Informe a senha do operador.'); return; }
    const item = cart.find(i => i.product_id === cancelItemTarget && !i.cancelled);
    if (!item) { setCancelItemTarget(null); return; }
    try {
      await axios.post(`${API}/sales/cancel-item`, {
        product_id: item.product_id, product_name: item.product_name,
        quantity: item.quantity, unit_price: item.unit_price, subtotal: item.subtotal,
        reason: cancelItemReason.trim(), operator_password: cancelItemPassword,
      }, { withCredentials: true });
    } catch (err) {
      setCancelItemError(err.response?.data?.detail || 'Erro ao registrar cancelamento');
      return;
    }
    const activeCount = cart.filter(i => !i.cancelled).length;
    const isLastActive = activeCount === 1;
    if (isLastActive) setCart([]);
    else {
      setCart(cart.map(i =>
        i.product_id === cancelItemTarget && !i.cancelled
          ? { ...i, cancelled: true, cancel_reason: cancelItemReason.trim() }
          : i
      ));
    }
    setCancelItemTarget(null);
    setCancelItemReason('');
    setCancelItemPassword('');
    setCancelItemError('');
  };

  const confirmDeleteItem = async () => {
    setDeleteItemError('');
    if (!deleteItemTarget) return;
    if (!deleteItemPassword) { setDeleteItemError('Informe a senha do operador.'); return; }
    const { item } = deleteItemTarget;
    try {
      await axios.post(`${API}/sales/cancel-item`, {
        product_id: item.product_id, product_name: item.product_name,
        quantity: item.quantity, unit_price: item.unit_price, subtotal: item.subtotal,
        reason: 'Exclusão de item no PDV (tecla Delete)', operator_password: deleteItemPassword,
      }, { withCredentials: true });
    } catch (err) {
      setDeleteItemError(err.response?.data?.detail || 'Erro ao excluir item');
      return;
    }
    const newCart = cartRef.current.filter(i => !(i.product_id === item.product_id && !i.cancelled));
    const remainingActive = newCart.filter(i => !i.cancelled);
    if (remainingActive.length === 0) {
      // Removeu o último item ativo — limpa o carrinho (mesmo comportamento do cancelamento)
      setCart([]);
      setSelectedItemIndex(-1);
      setDeleteItemTarget(null);
      setDeleteItemPassword('');
      setDeleteItemError('');
      setSuccess('Item excluído da venda.');
      setTimeout(() => setSuccess(''), 2500);
      return;
    }
    setCart(newCart);
    const newActive = newCart.map((it, i) => (it.cancelled ? -1 : i)).filter(i => i >= 0);
    let sel = -1;
    if (newActive.length) {
      const f = newActive.find(i => i >= deleteItemTarget.index);
      sel = f !== undefined ? f : newActive[newActive.length - 1];
    }
    setSelectedItemIndex(sel);
    setDeleteItemTarget(null);
    setDeleteItemPassword('');
    setDeleteItemError('');
    setSuccess('Item excluído da venda.');
    setTimeout(() => setSuccess(''), 2500);
  };

  const applyDiscount = () => {
    const val = parseFloat(discountInput);
    if (isNaN(val) || val < 0) { setError('Desconto inválido'); return; }
    if (val > 0 && (!discountReason || discountReason.trim().length < 3)) {
      setError('Justificativa obrigatória (mín. 3 caracteres). Selecione uma opção ou digite o motivo.');
      return;
    }
    setDiscount(val);
    setShowDiscountModal(false);
    setDiscountInput('');
    setError('');
  };

  const handleCancelSale = async () => {
    if (!cancelSaleReason.trim() || cancelSaleReason.trim().length < 3) {
      setError('Informe o motivo do cancelamento (mínimo 3 caracteres)');
      return;
    }
    if (!cancelSalePassword) {
      setError('Informe a senha do operador para confirmar o cancelamento');
      return;
    }
    const activeSaleItems = cart.filter(i => !i.cancelled);
    try {
      await axios.post(`${API}/sales/cancel-cart-sale`, {
        reason: cancelSaleReason.trim(),
        operator_password: cancelSalePassword,
        items: activeSaleItems.map(i => ({
          product_id: i.product_id, product_name: i.product_name,
          quantity: i.quantity, unit_price: i.unit_price, subtotal: i.subtotal,
        })),
        total: calculateTotal(),
      }, { withCredentials: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Erro ao registrar o cancelamento da venda');
      return; // mantém o modal aberto para o operador corrigir a senha/motivo
    }
    setCart([]);
    resetCustomer();
    setDiscount(0); setOnCredit(false); setCreditDueDate('');
    setLoadedPreSaleId(null);
    setShowCancelSaleModal(false); setCancelSaleReason(''); setCancelSalePassword(''); setError('');
    setSuccess('Venda cancelada e registrada no log.');
    setTimeout(() => setSuccess(''), 4000);
    if (pendingProceed) {
      setTimeout(() => { pendingProceed(); setPendingProceed(null); }, 50);
    }
  };

  const openReverseLastSale = async () => {
    if (cart.length > 0) {
      setError('Esvazie o carrinho antes de estornar a última venda.');
      setTimeout(() => setError(''), 4000);
      return;
    }
    setReverseLoading(true); setError('');
    try {
      const resp = await axios.get(`${API}/sales/last-by-operator`, { withCredentials: true });
      setReverseLastPreview(resp.data);
      setReverseLastReason('');
      setShowReverseLastModal(true);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Erro ao buscar última venda';
      setError(detail);
      setTimeout(() => setError(''), 4000);
    } finally {
      setReverseLoading(false);
    }
  };

  const confirmReverseLastSale = async () => {
    if (!reverseLastPreview) return;
    if (!reverseLastReason.trim() || reverseLastReason.trim().length < 3) {
      setError('Informe o motivo do estorno (mínimo 3 caracteres)');
      return;
    }
    setReverseLoading(true); setError('');
    try {
      await axios.post(
        `${API}/sales/${reverseLastPreview.id}/cancel`,
        { reason: reverseLastReason.trim() },
        { withCredentials: true }
      );
      const itemsToLoad = (reverseLastPreview.items || [])
        .filter(i => !i.cancelled)
        .map(i => ({
          product_id: i.product_id, product_name: i.product_name,
          quantity: i.quantity, unit_price: i.unit_price,
          cost_price: i.cost_price ?? 0, subtotal: i.subtotal, cancelled: false,
        }));
      setCart(itemsToLoad);
      setDiscount(reverseLastPreview.discount || 0);
      setSuccess(`Venda #${reverseLastPreview.sale_number} estornada. Itens carregados para correção.`);
      setTimeout(() => setSuccess(''), 5000);
      setShowReverseLastModal(false);
      setReverseLastPreview(null);
      setReverseLastReason('');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Erro ao estornar venda';
      setError(detail);
    } finally {
      setReverseLoading(false);
    }
  };

  const saveBackToTable = async () => {
    if (!loadedPreSaleId) { alert('Nenhuma mesa carregada para atualizar'); return; }
    const active = cart.filter(i => !i.cancelled);
    if (active.length === 0) { alert('A mesa precisa ter pelo menos 1 item'); return; }
    try {
      const items = active.map(i => ({
        product_id: i.product_id, product_name: i.product_name,
        quantity: i.quantity, unit_price: i.unit_price, subtotal: i.subtotal,
      }));
      await axios.put(`${API}/pre-sales/${loadedPreSaleId}`, {
        items,
        table_number: loadedTableNumber || undefined,
        title: loadedTableNumber ? `Mesa ${loadedTableNumber}` : undefined,
        customer_id: selectedCustomer || null,
        customer_name: customerName || null,
      }, { withCredentials: true });
      setSuccess(`✓ Mesa ${loadedTableNumber || ''} atualizada — itens salvos`);
      setCart([]);
      setLoadedPreSaleId(null);
      setLoadedTableNumber(null);
      setTimeout(() => setSuccess(''), 4000);
    } catch (e) {
      setError(e.response?.data?.detail || 'Erro ao salvar mesa');
    }
  };

  const handleFinalizeSale = async () => {
    // ETAPA 1: Valida a venda e o pagamento. Se OK, salva contexto e abre o modal
    // de escolha do tipo de cupom (fiscal / não fiscal). A gravação real da venda
    // acontece somente após o usuário confirmar o tipo no modal.
    const activeSaleItems = cart.filter(i => !i.cancelled);
    if (activeSaleItems.length === 0) { setError('Adicione produtos ao carrinho'); return; }
    const total = calculateTotal();

    // Sempre monta a lista de pagamentos a partir dos inputs (a_prazo é uma forma como as outras)
    let entries = Object.entries(paymentInputs)
      .map(([method, v]) => ({ method, amount: parseFloat(v) || 0 }))
      .filter(p => p.amount > 0);
    if (entries.length === 0) {
      setError('Informe o valor recebido em pelo menos uma forma de pagamento');
      return;
    }
    // Venda MISTA (à vista + a prazo): o crediário recebe SOMENTE o saldo restante (total − pago à vista),
    // nunca o total da venda — mesmo que o operador tenha selecionado A Prazo antes de digitar o dinheiro.
    if (entries.some(p => p.method === 'a_prazo')) {
      const paidCashLike = entries.filter(p => p.method !== 'a_prazo').reduce((s, p) => s + p.amount, 0);
      const restante = Math.round(Math.max(0, total - paidCashLike) * 100) / 100;
      entries = entries.filter(p => p.method !== 'a_prazo');
      if (restante > 0.005) entries.push({ method: 'a_prazo', amount: restante });
      const aPrazoStr = restante > 0.005 ? restante.toFixed(2) : '';
      if (aPrazoStr !== paymentInputs.a_prazo) setPaymentInputs(prev => ({ ...prev, a_prazo: aPrazoStr }));
    }
    const aPrazoAmount = entries.filter(p => p.method === 'a_prazo').reduce((s, p) => s + p.amount, 0);
    const hasCredit = aPrazoAmount > 0.005;
    if ((hasCredit || onCredit || paymentMethod === 'a_prazo') && !selectedCustomer) {
      setError('Selecione um cliente para venda a prazo');
      return;
    }
    const sum = entries.reduce((s, p) => s + p.amount, 0);
    const dinheiroVal = entries.find(p => p.method === 'dinheiro')?.amount || 0;
    const otherPaid = sum - dinheiroVal;
    if (otherPaid > total + 0.009) {
      setError(`Valor em cartão/PIX/outras formas (R$ ${otherPaid.toFixed(2)}) é maior que o total da venda (R$ ${total.toFixed(2)}). Troco só pode ser dado em dinheiro — corrija os valores.`);
      return;
    }
    const remainingForCash = Math.max(0, total - otherPaid);
    const troco = dinheiroVal > remainingForCash ? dinheiroVal - remainingForCash : 0;
    const effective = sum - troco;
    if (effective < total - 0.01) {
      setError(`Valor insuficiente. Falta R$ ${(total - effective).toFixed(2)}`);
      return;
    }
    const computedPayments = entries.map(p => {
      if (p.method === 'dinheiro' && troco > 0) {
        return { method: 'dinheiro', amount: Math.max(0, p.amount - troco) };
      }
      return p;
    }).filter(p => p.amount > 0);

    // Método primário salvo no banco:
    // - se a venda tiver QUALQUER valor em A Prazo -> 'a_prazo' (para o cupom mostrar "A Prazo", nunca "Cartão")
    // - senão, se for uma única forma -> essa forma; se mista sem a_prazo -> 'cartao'
    const isOnlyAPrazo = computedPayments.length === 1 && computedPayments[0].method === 'a_prazo';
    const isOnlyPix = computedPayments.length === 1 && computedPayments[0].method === 'pix';
    const hasAPrazo = computedPayments.some(p => p.method === 'a_prazo');
    const primaryMethod = hasAPrazo ? 'a_prazo' : (computedPayments.length === 1 ? computedPayments[0].method : 'cartao');
    const isFullOnCredit = isOnlyAPrazo; // só marca on_credit total se venda inteira é a_prazo (senão backend cria receivable parcial)

    const ctx = { computedPayments, isOnlyPix, primaryMethod, total, isFullOnCredit };
    setPendingSaleContext(ctx);
    setError('');
    setShowPaymentModal(false);

    // Se o operador ativou "Emitir NF-e" no card do sidebar, pula o modal
    // Fiscal/NãoFiscal e emite direto com cupom_type='nfe' (draft até plugar a API).
    if (nfeMode) {
      finalizeSaleWithCupomType('nfe', true, ctx);
    } else {
      setShowCupomModal(true);
    }
  };

  const cancelCupomChoice = () => {
    // Usuário voltou — reabre o modal de pagamento sem descartar os inputs
    setShowCupomModal(false);
    setPendingSaleContext(null);
    setShowPaymentModal(true);
  };

  const finalizeSaleWithCupomType = async (chosenType, shouldPrint = true, overrideCtx = null, overridePassword = null) => {
    // ETAPA 2: Executa o POST /api/sales com o cupom_type escolhido
    const ctx = overrideCtx || pendingSaleContext;
    if (!ctx) return;
    setCupomType(chosenType);
    setShowCupomModal(false);
    const { computedPayments, isOnlyPix, primaryMethod, total, isFullOnCredit } = ctx;

    setLoading(true); setError(''); setSuccess('');
    try {
      // PIX total OU misto (ex.: dinheiro + PIX): a cobrança Sicoob é gerada só pelo valor da parte em PIX.
      // A venda NÃO é gravada ainda — só depois que o PIX for confirmado (ou o operador registrar manualmente).
      // Cancelar/expirar o QR descarta a cobrança e volta para a escolha da forma de pagamento com o carrinho intacto.
      const pixPart = !isFullOnCredit ? (computedPayments || []).find(p => p.method === 'pix' && p.amount > 0) : null;
      if (pixPart && !ctx.pixCharge) {
        const orderId = `ORDER-${Date.now()}`;
        const pixAmount = Math.round(pixPart.amount * 100) / 100;
        let charge = null;
        // 1º tenta o PIX automático Sicoob (cobrança dinâmica + confirmação por webhook); se desativado, QR estático (só PIX integral)
        try {
          const ch = await axios.post(`${API}/pix/sicoob/charge`, { amount: pixAmount, order_id: orderId, customer_name: customerName, customer_cpf: customerCpf }, { withCredentials: true });
          charge = { txid: ch.data.txid, provider: 'sicoob' };
          setPixData({ provider: 'sicoob', txid: ch.data.txid, qr_code: ch.data.brcode, amount: pixAmount, total, mixed: !isOnlyPix, status: ch.data.status, expiracao: ch.data.expiracao });
        } catch (pe) {
          const st = pe.response?.status;
          const detail = pe.response?.data?.detail || '';
          // 424 = Sicoob recusou/sem comunicação; 400 com Sicoob ATIVADO (chave/cert) = configuração incompleta → erro visível, nunca QR estático silencioso
          if (st && st !== 400) throw pe;
          if (st === 400 && !/desativado/i.test(detail)) throw pe;
          if (isOnlyPix) {
            const pixResponse = await axios.post(`${API}/pix/create`, {
              order_id: orderId, amount: pixAmount, customer_name: customerName, customer_cpf: customerCpf
            }, { withCredentials: true });
            charge = { txid: null, provider: 'static' };
            setPixData({ provider: 'static', ...pixResponse.data });
          }
        }
        if (charge) {
          pixPendingRef.current = { ctx: { ...ctx, pixCharge: charge }, chosenType, shouldPrint, overridePassword };
          return; // continua em closePix (confirmado) ou cancelPix (cancelado/expirado)
        }
      }
      const sicoobTxid = ctx.pixCharge?.provider === 'sicoob' ? ctx.pixCharge.txid : null;
      const saleData = {
        items: cart, payment_method: primaryMethod,
        payments: (computedPayments && computedPayments.length > 0) ? computedPayments : undefined,
        cupom_type: chosenType, discount,
        discount_reason: discount > 0 ? (discountReason || null) : null,
        total,
        customer_id: selectedCustomer || undefined,
        customer_name: customerName || undefined,
        customer_cpf: customerCpf || undefined,
        on_credit: isFullOnCredit,
        manager_override_password: overridePassword || undefined,
        credit_due_date: (isFullOnCredit || (computedPayments || []).some(p => p.method === 'a_prazo')) && creditDueDate ? new Date(creditDueDate).toISOString() : undefined,
        delivery: delivery ? {
          name: delivery.name, phone: delivery.phone, address: delivery.address,
          neighborhood: delivery.neighborhood || undefined,
          reference: delivery.reference || undefined,
          notes: delivery.notes || undefined,
        } : undefined,
      };
      const saleResp = await axios.post(`${API}/sales`, saleData, { withCredentials: true });
      if (sicoobTxid) {
        axios.put(`${API}/pix/sicoob/charge/${sicoobTxid}/sale`, { sale_id: saleResp.data.id }, { withCredentials: true }).catch(() => {});
      }

      // NFC-e: "Cupom Fiscal" e "Emitir NF-e" transmitem à SEFAZ (via Focus NFe) logo após registrar a venda
      // e anexam o resultado ao cupom (QR Code, chave, protocolo). NFC-e desativada → cupom fiscal simples.
      const wantsNfce = chosenType === 'nfe' || (chosenType === 'fiscal' && company?.nfce_enabled);
      if (wantsNfce) {
        try {
          const nf = await axios.post(`${API}/nfce/issue/${saleResp.data.id}`, {}, { withCredentials: true });
          saleResp.data = { ...saleResp.data, nfce: nf.data };
          if (nf.data.status === 'erro_autorizacao' || nf.data.status === 'denegado') {
            setError(`SEFAZ rejeitou a NFC-e da venda #${saleResp.data.sale_number}: ${nf.data.mensagem_sefaz || 'erro desconhecido'}. O cupom sai como não fiscal — corrija e reemita em Vendas.`);
          } else if (nf.data.status === 'contingencia_pendente') {
            setError(`SEFAZ indisponível — NFC-e nº ${nf.data.number} emitida em CONTINGÊNCIA OFFLINE; será transmitida automaticamente.`);
          }
        } catch (nfErr) {
          setError(`NFC-e não emitida: ${nfErr.response?.data?.detail || nfErr.message}. O cupom sai como não fiscal — reemita em Vendas.`);
        }
      }

      if (loadedPreSaleId) {
        try {
          await axios.post(`${API}/pre-sales/${loadedPreSaleId}/mark-finalized?sale_id=${saleResp.data.id}`, {}, { withCredentials: true });
        } catch (_e) { /* non-critical */ }
        setLoadedPreSaleId(null);
        setLoadedTableNumber(null);
      }

      {
        const nf = saleResp.data.nfce;
        const cupomLabel =
          nf?.status === 'autorizado' ? `NFC-e nº ${nf.number} AUTORIZADA${nf.ambiente !== 'producao' ? ' (homologação)' : ''}` :
          nf?.status === 'draft_local' ? `NFC-e rascunho nº ${nf.number}` :
          chosenType === 'fiscal' || chosenType === 'nfe' ? 'Fiscal' : 'Não Fiscal';
        const hasCredit = (computedPayments || []).some(p => p.method === 'a_prazo');
        const pixPrefix = ctx.pixCharge ? (ctx.pixPaid ? 'PIX recebido (Sicoob)! ' : ctx.pixCharge.provider === 'sicoob' ? 'PIX aguardando confirmação (concilie em Vendas). ' : '') : '';
        setSuccess(hasCredit
          ? `${pixPrefix}Venda registrada! ${isFullOnCredit ? 'Toda' : 'Parte'} a prazo — conta a receber criada para ${customerName}.`
          : `${pixPrefix}Venda realizada! Cupom ${cupomLabel} emitido.`
        );
        if (shouldPrint) setReceiptSale(saleResp.data); // impressão direta (sem prévia) — 'Não Imprimir' pula
        else {
          const sp = saleResp.data;
          const paidCash = (sp.payments || []).some((p) => p.method === 'dinheiro' && Number(p.amount) > 0) || (!sp.payments?.length && sp.payment_method === 'dinheiro');
          if (paidCash) openCashDrawer();
          focusBarcode();
        }
        if (hasCredit && selectedCustomer) {
          const creditAmount = isFullOnCredit ? total : (computedPayments || []).filter(p => p.method === 'a_prazo').reduce((s, p) => s + p.amount, 0);
          const custIdForVoucher = selectedCustomer;
          const custNameForVoucher = customerName;
          try {
            const cr = await axios.get(`${API}/customers/${custIdForVoucher}`, { withCredentials: true });
            printFiadoVoucher({
              company,
              customerName: custNameForVoucher,
              saleNumber: saleResp.data.sale_number,
              creditAmount,
              newBalance: cr.data.balance,
              creditLimit: cr.data.credit_limit,
            });
          } catch (_e) { /* impressão do comprovante é não-crítica */ }
        }
        setCart([]);
        resetCustomer();
        setDiscount(0); setOnCredit(false); setCreditDueDate('');
        setDiscountReason('');
        setNfeMode(false);
        setPaymentInputs({ dinheiro: '', pix: '', pix_maquininha: '', credito: '', debito: '', voucher: '', cheque: '', a_prazo: '' });
        setShowPaymentModal(false);
        setCreditOverride({ show: false, info: null, ctx: null, chosenType: null, password: '', error: '' });
        setPendingWeight(null); pendingWeightRef.current = null;
        setTimeout(() => setSuccess(''), 4000);
        focusBarcode();
      }
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 403 && detail && detail.code === 'credit_blocked') {
        setError(detail.message);
        return;
      }
      if (status === 403 && detail && detail.code === 'credit_limit_exceeded') {
        setCreditOverride({ show: true, info: detail, ctx, chosenType, shouldPrint, password: '', error: '' });
        return;
      }
      if (status === 401 && detail && detail.code === 'override_invalid') {
        setCreditOverride((o) => ({ ...o, show: true, ctx, chosenType, shouldPrint, error: detail.message, password: '' }));
        return;
      }
      setError(typeof detail === 'string' ? detail : (detail?.message || 'Erro ao finalizar venda'));
    } finally {
      setLoading(false);
      setPendingSaleContext(null);
    }
  };

  const submitCreditOverride = () => {
    const { ctx, chosenType, password, shouldPrint } = creditOverride;
    if (!password) { setCreditOverride((o) => ({ ...o, error: 'Digite a senha do administrador' })); return; }
    setCreditOverride((o) => ({ ...o, show: false, error: '' }));
    finalizeSaleWithCupomType(chosenType, shouldPrint !== false, ctx, password);
  };

  const cancelCreditOverride = () => {
    setCreditOverride({ show: false, info: null, ctx: null, chosenType: null, password: '', error: '' });
    setError('Venda cancelada — limite de crédito do cliente excedido.');
    setTimeout(() => setError(''), 4000);
  };

  // PIX confirmado (Sicoob) ou registrado manualmente → agora sim grava a venda, emite NFC-e e imprime
  const closePix = (paid = false) => {
    const pending = pixPendingRef.current;
    pixPendingRef.current = null;
    setPixData(null);
    if (!pending) { focusBarcode(); return; }
    finalizeSaleWithCupomType(pending.chosenType, pending.shouldPrint, { ...pending.ctx, pixPaid: !!paid }, pending.overridePassword);
  };

  // Cancelado pelo operador ou QR expirado (2 min) → descarta a cobrança, mantém o carrinho e volta à forma de pagamento
  const cancelPix = (reason = 'cancelado') => {
    pixPendingRef.current = null;
    setPixData(null);
    setPendingSaleContext(null);
    setLoading(false);
    setError(reason === 'expirado'
      ? 'Tempo do QR Code PIX esgotado (2 min) sem confirmação — a venda não foi registrada. Escolha a forma de pagamento novamente.'
      : 'PIX cancelado — a venda não foi registrada. Escolha a forma de pagamento.');
    setTimeout(() => setError(''), 6000);
    setShowPaymentModal(true);
  };

  // ============== RENDER ==============
  return (
    <Layout>
      <div className="pdv-root flex flex-col lg:flex-row h-full overflow-y-auto lg:overflow-hidden" data-testid="pdv-page" data-pdv-theme={pdvTheme}>
        <PDVCartList
          company={company}
          cart={cart}
          updateQuantity={updateQuantity}
          cancelItem={cancelItem}
          selectedItemIndex={selectedItemIndex}
          error={error}
          success={success}
        />
        <PDVSidebar
          barcodeInput={barcodeInput}
          setBarcodeInput={setBarcodeInput}
          pendingWeight={pendingWeight}
          clearPendingWeight={() => { setPendingWeight(null); pendingWeightRef.current = null; }}
          handleManualBarcode={handleManualBarcode}
          processInput={processInput}
          calculateTotal={calculateTotal}
          calculateSubtotal={calculateSubtotal}
          discount={discount}
          activeItems={activeItems}
          cancelledItems={cancelledItems}
          setShowDiscountModal={setShowDiscountModal}
          selectedCustomer={selectedCustomer}
          customerName={customerName}
          customerCpf={customerCpf}
          nfeMode={nfeMode}
          setNfeMode={setNfeMode}
          setShowPaymentModal={setShowPaymentModal}
          hasActiveItems={hasActiveItems}
          setShowCancelSaleModal={setShowCancelSaleModal}
          openReverseLastSale={openReverseLastSale}
          reverseLoading={reverseLoading}
          cart={cart}
          setShowSearch={setShowSearch}
          setShowCalculator={setShowCalculator}
          setShowReceivablesModal={setShowReceivablesModal}
          setShowPreSalesModal={setShowPreSalesModal}
          setShowDeliveryModal={setShowDeliveryModal}
          delivery={delivery}
          loadedTableNumber={loadedTableNumber}
          saveBackToTable={saveBackToTable}
          setCart={setCart}
          setLoadedPreSaleId={setLoadedPreSaleId}
          setLoadedTableNumber={setLoadedTableNumber}
          user={user}
          setShowSwitchOperator={setShowSwitchOperator}
          setShowCloseShift={setShowCloseShift}
          setShowSangriaModal={setShowSangriaModal}
          setShowSuprimentoModal={setShowSuprimentoModal}
        />
      </div>

      <PDVPaymentModal
        show={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        calculateTotal={calculateTotal}
        paymentInputs={paymentInputs}
        setPaymentInputs={setPaymentInputs}
        onCredit={onCredit}
        setOnCredit={setOnCredit}
        setPaymentMethod={setPaymentMethod}
        paymentMethod={paymentMethod}
        creditDueDate={creditDueDate}
        setCreditDueDate={setCreditDueDate}
        customers={customers}
        selectedCustomer={selectedCustomer}
        setSelectedCustomer={setSelectedCustomer}
        customerSearch={customerSearch}
        setCustomerSearch={setCustomerSearch}
        customerHoverIdx={customerHoverIdx}
        setCustomerHoverIdx={setCustomerHoverIdx}
        customerName={customerName}
        setCustomerName={setCustomerName}
        customerCpf={customerCpf}
        setCustomerCpf={setCustomerCpf}
        handleFinalizeSale={handleFinalizeSale}
        loading={loading}
      />

      <PDVCupomModal
        show={showCupomModal}
        total={pendingSaleContext?.total}
        onConfirm={finalizeSaleWithCupomType}
        onCancel={cancelCupomChoice}
        defaultOption={company?.cupom_default_option || 'none'}
      />

      <PixModal key={pixData?.txid || pixData?.order_id || 'none'} pixData={pixData} onClose={closePix} onCancel={cancelPix} />

      <Calculator open={showCalculator} onClose={() => setShowCalculator(false)} />
      <ProductSearchModal
        open={showSearch}
        onClose={() => setShowSearch(false)}
        onSelect={(p) => addProductToCart(p)}
      />
      {receiptSale && <ReceiptModal sale={receiptSale} onClose={() => { setReceiptSale(null); focusBarcode(); }} silent />}
      {stockWarning && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" data-testid="stock-warning-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
            <div className="bg-amber-500 px-5 py-4 flex items-center gap-3">
              <AlertTriangle size={26} className="text-white" />
              <div>
                <h3 className="text-lg font-black text-white leading-tight">Atenção: produto sem estoque</h3>
                <p className="text-amber-50 text-sm">O item pode ser lançado mesmo assim.</p>
              </div>
            </div>
            <div className="p-5">
              <p className="text-slate-700 text-sm">
                <span className="font-bold">{stockWarning.product.name}</span> está com estoque{' '}
                <span className="font-bold text-amber-700">
                  {Number(stockWarning.product.stock_quantity).toLocaleString('pt-BR')} {stockWarning.product.unit || 'un'}
                </span>.
              </p>
              <p className="text-slate-500 text-sm mt-2">Deseja lançar o item mesmo assim?</p>
            </div>
            <div className="px-5 pb-5 flex gap-3">
              <button
                onClick={() => { setStockWarning(null); focusBarcode(); }}
                className="flex-1 h-11 rounded-md border border-slate-300 font-bold text-slate-700 hover:bg-slate-50"
                data-testid="stock-warning-cancel-btn"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  const sw = stockWarning;
                  setStockWarning(null);
                  addProductToCart(sw.product, sw.scaleWeight, sw.scalePrice, true);
                  focusBarcode();
                }}
                className="flex-1 h-11 rounded-md bg-amber-500 hover:bg-amber-600 font-bold text-white"
                data-testid="stock-warning-confirm-btn"
              >
                Lançar mesmo assim
              </button>
            </div>
          </div>
        </div>
      )}

      <CancelItemModal
        target={cancelItemTarget}
        cart={cart}
        reason={cancelItemReason}
        setReason={setCancelItemReason}
        password={cancelItemPassword}
        setPassword={setCancelItemPassword}
        error={cancelItemError}
        setError={setCancelItemError}
        onCancel={() => setCancelItemTarget(null)}
        onConfirm={confirmCancelItem}
      />

      <DeleteItemPasswordModal
        target={deleteItemTarget}
        password={deleteItemPassword}
        setPassword={setDeleteItemPassword}
        error={deleteItemError}
        onCancel={() => { setDeleteItemTarget(null); setDeleteItemPassword(''); setDeleteItemError(''); }}
        onConfirm={confirmDeleteItem}
      />

      <CancelSaleModal
        show={showCancelSaleModal}
        cart={cart}
        reason={cancelSaleReason}
        setReason={setCancelSaleReason}
        password={cancelSalePassword}
        setPassword={setCancelSalePassword}
        error={error}
        onClose={() => { setShowCancelSaleModal(false); setCancelSaleReason(''); setCancelSalePassword(''); setError(''); }}
        onConfirm={handleCancelSale}
      />

      <NavBlockModal
        show={!!pendingProceed && !showCancelSaleModal}
        cart={cart}
        onStay={() => setPendingProceed(null)}
        onCancelSale={() => setShowCancelSaleModal(true)}
      />

      <PreSalesModal
        open={showPreSalesModal}
        onClose={() => setShowPreSalesModal(false)}
        currentCart={cart}
        currentTitle={null}
        currentCustomer={selectedCustomer ? { id: selectedCustomer, name: customerName } : null}
        loadedPreSaleId={loadedPreSaleId}
        loadedTableNumber={loadedTableNumber}
        onSent={({ tableNumber: tn }) => {
          setCart([]); setDiscount(0); setDiscountInput('');
          resetCustomer();
          setOnCredit(false); setLoadedPreSaleId(null); setLoadedTableNumber(null);
          setSuccess(`✓ Itens enviados para Mesa ${tn}. PDV liberado.`);
        }}
        onLoad={(ps) => {
          const loaded = ps.items.map(i => ({
            product_id: i.product_id, product_name: i.product_name,
            quantity: i.quantity, unit_price: i.unit_price, subtotal: i.subtotal,
            cancelled: false,
          }));
          setCart(loaded);
          if (ps.customer_id) { setSelectedCustomer(ps.customer_id); setCustomerName(ps.customer_name || ''); }
          setLoadedPreSaleId(ps.id);
          setLoadedTableNumber(ps.table_number || null);
        }}
      />

      <DiscountModal
        show={showDiscountModal}
        discountInput={discountInput}
        setDiscountInput={setDiscountInput}
        discountReason={discountReason}
        setDiscountReason={setDiscountReason}
        discount={discount}
        calculateSubtotal={calculateSubtotal}
        applyDiscount={applyDiscount}
        onClearAndClose={() => { setDiscount(0); setDiscountReason(''); setShowDiscountModal(false); setDiscountInput(''); }}
        error={error}
        onClose={() => { setShowDiscountModal(false); setError(''); }}
      />

      <ReverseLastModal
        show={showReverseLastModal}
        preview={reverseLastPreview}
        reason={reverseLastReason}
        setReason={setReverseLastReason}
        loading={reverseLoading}
        error={error}
        onClose={() => { setShowReverseLastModal(false); setReverseLastPreview(null); setReverseLastReason(''); setError(''); }}
        onConfirm={confirmReverseLastSale}
      />

      <PDVReceivablesModal
        open={showReceivablesModal}
        onClose={() => { setShowReceivablesModal(false); focusBarcode(); }}
        onReceived={(r) => { if (r?.message) { setSuccess(r.message); setTimeout(() => setSuccess(''), 6000); } }}
      />
      <DeliveryModal
        open={showDeliveryModal}
        onClose={() => setShowDeliveryModal(false)}
        onConfirm={(data) => { setDelivery(data); setSuccess(`Entrega cadastrada: ${data.name}`); setTimeout(() => setSuccess(''), 4000); }}
      />
      <SwitchOperatorModal
        open={showSwitchOperator}
        onClose={() => setShowSwitchOperator(false)}
        currentUser={user}
      />
      <CloseShiftModal
        open={showCloseShift}
        onClose={() => setShowCloseShift(false)}
        onClosed={() => { /* modal decide o redirect (/login) — evita race condition */ }}
      />
      <SangriaModal
        show={showSangriaModal}
        onClose={() => setShowSangriaModal(false)}
        onSuccess={(data) => {
          setSuccess(`Sangria de R$ ${Number(data.amount).toFixed(2)} registrada. Comprovante impresso.`);
          setTimeout(() => setSuccess(''), 5000);
        }}
      />
      <SuprimentoModal
        show={showSuprimentoModal}
        onClose={() => setShowSuprimentoModal(false)}
        onSuccess={(data) => {
          setSuccess(`Suprimento de R$ ${Number(data.amount).toFixed(2)} registrado. Comprovante impresso.`);
          setTimeout(() => setSuccess(''), 5000);
        }}
      />
      <CreditLimitOverrideModal
        show={creditOverride.show}
        info={creditOverride.info}
        password={creditOverride.password}
        setPassword={(v) => setCreditOverride((o) => ({ ...o, password: v }))}
        error={creditOverride.error}
        loading={loading}
        onConfirm={submitCreditOverride}
        onCancel={cancelCreditOverride}
      />
    </Layout>
  );
};
